import { useContext, useEffect, useRef, useState } from 'react';
import JsBarcode from 'jsbarcode';

import AdminModal from '../../reusable/admin-modal/admin-modal.component';
import Button from '../../reusable/button/button.component';
import InputCurrency from '../../reusable/inputs/currency.input';
import Spinner from '../../reusable/spinner/spinner.component';

import { ToastContext } from '../../../contexts/toast.context';
import Client from '../../../tools/client';

import {
  ContentContainer,
  DeleteButton,
  Input,
  MainContainer,
  Option,
  RowContainer,
  Select
} from '../../../styles/component.styles';

const client = new Client();

const UpdateInventory = ({ inventory, onUpdate = async () => {}, onCancel = () => {} }) => {
  const [ loading, setLoading ] = useState(false);
  const [ showModal, setShowModal ] = useState(false);

  const [ product, setProduct ] = useState(inventory.Product);
  const [ inventoryId ] = useState(inventory.id);
  const [ type, setType ] = useState(inventory.type);
  const [ price, setPrice ] = useState(inventory.price);
  const [ size, setSize ] = useState(inventory.size);
  const [ sizeDescription, setSizeDescription ] = useState(inventory.sizeDescription);
  const [ sku, setSku ] = useState(inventory.sku);
  const [ addressOne, setAddressOne ] = useState(inventory.address?.addressOne || '');
  const [ addressTwo, setAddressTwo ] = useState(inventory.address?.addressTwo || '');
  const [ city, setCity ] = useState(inventory.address?.city || '');
  const [ state, setState ] = useState(inventory.address?.state || '');
  const [ zipCode, setZipCode ] = useState(inventory.address?.zipCode || '');
  const [ bay, setBay ] = useState(inventory.bay);

  const barcodeRef = useRef(null);
  const [ barcodeError, setBarcodeError ] = useState(false);

  const { errorToast, successToast } = useContext(ToastContext);

  const generateBarcodeValue = () => `GSI-${Date.now()}`;

  const populateNewSku = () => {
    setSku(generateBarcodeValue());
  };

  useEffect(() => {
    if (!sku || !barcodeRef.current) return;

    try {
      JsBarcode(barcodeRef.current, sku, {
        format: 'CODE128',
        lineColor: '#000',
        width: 2,
        height: 40,
        displayValue: true
      });
      setBarcodeError(false);
    } catch (err) {
      setBarcodeError(true);
    }
  }, [sku]);

  const checkFields = () => {
    if (
      type === '' ||
      price === '' ||
      size === '' ||
      sizeDescription === '' ||
      sku === ''
    ) {
      return {
        result: false,
        error: 'Please complete all fields to update inventory'
      };
    }

    return { result: true };
  };

  const updateInventory = async () => {
    setLoading(true);
    const checkFieldsResult = checkFields();
    if (checkFieldsResult.error) {
      errorToast(checkFieldsResult.error);
      setLoading(false);
      return;
    }

    const data = {
      id: inventoryId,
      type,
      price,
      size,
      sizeDescription,
      sku,
      address: {
        addressOne,
        addressTwo,
        city,
        state,
        zipCode
      },
      bay
    };

    await client.updateInventory(data);
    await onUpdate();
    successToast('Updated Inventory');
    setLoading(false);
  };

  const confirmDelete = () => setShowModal(true);

  const deleteInventory = async () => {
    const res = await client.deleteInventory({ id: inventoryId });
    if (res.status !== 200) {
      errorToast(res.message);
      return;
    }

    await onUpdate();
  };

  const dropdownDisplay = () => {
    if (product?.ProductType?.name === 'Seeds') {
      return (
        <>
          <RowContainer margin="10px 0">
            <Select name="inventoryType" value={type} onChange={(e) => setType(e.target.value)}>
              <Option value="" disabled> -- Seed Type -- </Option>
              <Option value="regular">Regular</Option>
              <Option value="feminized">Feminized</Option>
            </Select>
          </RowContainer>
          <RowContainer margin="10px 0">
            <Select name="size" value={size} onChange={(e) => setSize(e.target.value)}>
              <Option value="" disabled> -- Pack Size -- </Option>
              <Option value="Full Pack">Full Pack</Option>
              <Option value="Half Pack">Half Pack</Option>
            </Select>
          </RowContainer>
        </>
      );
    }

    if (product?.ProductType?.name === 'Clones') {
      return (
        <>
          <RowContainer margin="10px 0">
            <Select name="inventoryType" value={type} onChange={(e) => setType(e.target.value)}>
              <Option value="" disabled> -- Clone Sex -- </Option>
              <Option value="female">Female</Option>
              <Option value="male">Male</Option>
            </Select>
          </RowContainer>
          <RowContainer margin="10px 0">
            <Select name="inventorySize" value={size} onChange={(e) => setSize(e.target.value)}>
              <Option value="" disabled> -- Clone Type -- </Option>
              <Option value="rooted">Rooted</Option>
              <Option value="snips">Snips</Option>
            </Select>
          </RowContainer>
        </>
      );
    }
  };

  return (
    <MainContainer>
      <AdminModal
        show={showModal}
        setShow={setShowModal}
        title="Delete Inventory"
        message={`Are you sure you want to delete inventory ${size}, ${sizeDescription}?`}
        action={() => deleteInventory()}
        actionText="Delete"
      />
      {loading ? (
        <Spinner />
      ) : (
        <ContentContainer>
          <InputCurrency value={price} onChange={setPrice} />
          {dropdownDisplay()}
          <Input type="text" value={sizeDescription} onChange={(e) => setSizeDescription(e.target.value)} placeholder="Size Description" />
          <Input type="text" value={sku} onChange={(e) => setSku(e.target.value)} placeholder="SKU" />
          <button onClick={populateNewSku}>Create New SKU</button>
          {barcodeError ? (
            <div style={{ color: 'red' }}>
              Invalid SKU for barcode. Try changing it or click "Create New SKU".
            </div>
          ) : (
            <svg ref={barcodeRef} />
          )}
          <Input type="text" value={addressOne} onChange={(e) => setAddressOne(e.target.value)} placeholder="Address One" />
          <Input type="text" value={addressTwo} onChange={(e) => setAddressTwo(e.target.value)} placeholder="Address Two" />
          <Input type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
          <Input type="text" value={state} onChange={(e) => setState(e.target.value)} placeholder="State" />
          <Input type="text" value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="Zip Code" />
          <Input type="text" value={bay} onChange={(e) => setBay(e.target.value)} placeholder="Bay" />

          <RowContainer margin="20px 0">
            <Button onClick={() => onCancel()}>Cancel</Button>
            <Button onClick={updateInventory}>Update Inventory</Button>
          </RowContainer>

          <ContentContainer>
            <DeleteButton onClick={confirmDelete}>Delete Inventory</DeleteButton>
          </ContentContainer>
        </ContentContainer>
      )}
    </MainContainer>
  );
};

export default UpdateInventory;